import { appFetch } from "./appFetch.js";
import { supabase } from "./supabaseClient";

// True when the error looks like a transient network failure that's worth
// retrying once. Chrome's Memory Saver / tab-discard can leave the previous
// HTTP/2 connection in a half-closed state; the FIRST request after wake-up
// frequently surfaces as `TypeError: Failed to fetch` even though the network
// is fine and the second attempt 100ms later succeeds.
const isTransientFetchError = (e) => {
  if (!e) return false;
  const msg = String(e.message || e.name || "");
  return /Failed to fetch|NetworkError|Network request failed|fetch failed|AuthRetryableFetchError/i.test(msg);
};

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

async function withRetry(fn) {
  try {
    return await fn();
  } catch (e) {
    if (!isTransientFetchError(e)) throw e;
    // Brief backoff, then a single retry.
    await sleep(200);
    return await fn();
  }
}

// ── Single refresh authority ───────────────────────────────────────────────
// All token refreshes go through here. Two guarantees that keep users logged
// in for weeks (Setmore-style) instead of bouncing them to login:
//   1. SERIALIZED — concurrent callers share one in-flight refresh, so we never
//      fire two refreshes that race into Supabase's "refresh token already
//      used" rotation error (the #1 cause of random logouts).
//   2. CLASSIFIED — the result says whether the failure means the refresh token
//      is *definitively* invalid (`authInvalid` → safe to log out) vs a
//      transient network/timeout/5xx (`transient` → KEEP the user logged in and
//      let the next request retry). Callers must only log out on authInvalid.
const _isNative = () => typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();

// ── NATIVE token refresh, over plain fetch ─────────────────────────────────
//
// On Android, supabase-js's auth calls do not come back. Not slowly — at all:
// measured on a device, refreshSession() was still unresolved after 60s, and
// .from()/getSession() behave the same way. They share one cause — every one
// of them awaits the client's own session/init machinery, which is the same
// wedge that made rawRead/rawWrite necessary for data.
//
// The damage was delayed and therefore easy to miss: the app works fine for
// an hour, then the access token expires, the refresh that should renew it
// hangs, and every read and write 401s with "JWT expired". Reproduced exactly
// that way on 2026-09-19 — the shop app sat happily on the schedule, then
// dropped to the login screen an hour after sign-in.
//
// So native refreshes talk to GoTrue directly and persist the result itself.
// The stored value is the Session object as supabase-js writes it (see
// _saveSession in auth-js), so the client picks it up on the next cold start.
// Everything native reads for authorization — window.__petosAccessToken for
// raw fetches, realtime.setAuth for the socket — is updated here too.
async function refreshSessionNative() {
  const transient = { ok: false, authInvalid: false, transient: true, session: null };
  let Preferences;
  try { ({ Preferences } = await import("@capacitor/preferences")); }
  catch { return transient; }
  const KEY = "petos-auth";
  let stored = null;
  try {
    const { value } = await Preferences.get({ key: KEY });
    stored = value ? JSON.parse(value) : null;
  } catch { return transient; }
  const refreshToken = stored?.refresh_token || stored?.currentSession?.refresh_token || null;
  // No stored refresh token is NOT "your login is invalid" — it's "there is no
  // session here to refresh". Logging out on this would boot a user who had
  // simply never signed in on this device.
  if (!refreshToken) return transient;

  const base = String(import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY || "";
  const ctrl = new AbortController();
  const tid = setTimeout(() => { try { ctrl.abort(); } catch { /* ignore */ } }, 12000);
  let resp = null, body = null;
  try {
    resp = await appFetch(base + "/auth/v1/token?grant_type=refresh_token", {
      method: "POST",
      signal: ctrl.signal,
      timeoutMs: 12000,
      cache: "no-store",
      headers: { "Content-Type": "application/json", apikey: anon, Authorization: "Bearer " + anon },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    body = await resp.json().catch(() => null);
  } catch {
    return transient;               // network / abort — never a logout reason
  } finally { clearTimeout(tid); }

  if (!resp.ok) {
    const m = String(body?.error_description || body?.msg || body?.error || "").toLowerCase();
    // Only a definitively dead refresh token justifies a logout. A 5xx or a
    // rate-limit is the server having a bad minute, not the user being signed out.
    const authInvalid = (resp.status === 400 || resp.status === 401)
      && /refresh token|invalid grant|already used|not found|revoked|expired/.test(m);
    return { ok: false, authInvalid, transient: !authInvalid, session: null };
  }
  const session = body;
  if (!session?.access_token || !session?.refresh_token) return transient;
  session.expires_at = session.expires_at
    || Math.floor(Date.now() / 1000) + (Number(session.expires_in) || 3600);
  try { await Preferences.set({ key: KEY, value: JSON.stringify(session) }); }
  catch { /* the token below still works for this run */ }
  try { window.__petosAccessToken = session.access_token; } catch { /* ignore */ }
  try { supabase?.realtime?.setAuth?.(session.access_token); } catch { /* ignore */ }
  return { ok: true, authInvalid: false, transient: false, session };
}

let _refreshInflight = null;
export async function ensureFreshSession() {
  if (_refreshInflight) return _refreshInflight;
  _refreshInflight = (async () => {
    try {
      // HARD CAP on the refresh.
      //
      // Every caller here shares one in-flight promise so refreshes stay
      // serialized (two racing refreshes rotate each other's token and log the
      // user out). The flaw was that a refresh which NEVER SETTLES therefore
      // wedges refreshing FOREVER — every later caller is handed the same dead
      // promise, and _refreshInflight is only cleared once it resolves.
      //
      // That is not hypothetical: measured on Android, supabase.auth
      // .refreshSession() did not settle in 60+ seconds, so from the first
      // hung call the app could never refresh its token again — and once the
      // access token expires, every read and write 401s.
      //
      // A cap makes it always settle. A timeout is classified TRANSIENT, which
      // is never a logout reason, so the worst case is an unchanged token and
      // a retry on the next tick — the same as any network blip.
      if (_isNative()) return await refreshSessionNative();
      const { data, error } = await Promise.race([
        supabase.auth.refreshSession(),
        new Promise((resolve) => setTimeout(() => resolve({ data: null, error: { message: "refresh-timeout", status: 0 } }), 15000)),
      ]);
      if (error) {
        const m = String(error.message || "").toLowerCase();
        const status = Number(error.status) || 0;
        const transient = isTransientFetchError(error) || status >= 500 || status === 0;
        // A 400/401 from the token endpoint with refresh-token language means
        // the refresh token is gone/used/revoked — the only real logout reason.
        const authInvalid = !transient && (status === 400 || status === 401 ||
          /refresh token|invalid grant|already used|not found|revoked|expired/.test(m));
        return { ok: false, authInvalid, transient, session: null };
      }
      const session = data?.session || null;
      if (!session?.user) return { ok: false, authInvalid: false, transient: true, session: null };
      try { window.__petosAccessToken = session.access_token || null; } catch { /* ignore */ }
      return { ok: true, authInvalid: false, transient: false, session };
    } catch (e) {
      // Thrown (network / abort / timeout) → transient, never a logout reason.
      return { ok: false, authInvalid: false, transient: true, session: null };
    }
  })();
  try { return await _refreshInflight; }
  finally { _refreshInflight = null; }
}

export async function signIn({ email, password }) {
  return withRetry(async () => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  });
}

export async function signUp({ email, password, name, phone, role = "customer", tenant_id = null }) {
  return withRetry(async () => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name, phone, role, tenant_id } },
    });
    if (error) throw error;
    return data;
  });
}

const _nativeApp = () => typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();

export async function signOut() {
  // Native (Android especially): supabase.auth.signOut() never settles in
  // the WebView, so logout used to hang and the user stayed signed in.
  // Revoke the session server-side without waiting, then delete the stored
  // session (Capacitor Preferences) ourselves — supabase-js re-reads storage,
  // so getSession() is null from here on.
  if (_nativeApp()) {
    const token = (() => { try { return window.__petosAccessToken || null; } catch { return null; } })();
    try {
      const url = String(import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      if (url && key && token) {
        fetch(url + "/auth/v1/logout?scope=local", { method: "POST", headers: { apikey: key, Authorization: "Bearer " + token } }).catch(() => {});
      }
    } catch { /* best effort */ }
    try { await Promise.race([supabase.auth.signOut({ scope: "local" }).catch(() => {}), new Promise(r => setTimeout(r, 1500))]); } catch { /* ignore */ }
    try {
      const { Preferences } = await import("@capacitor/preferences");
      for (const k of ["petos-auth", "petos-auth-user", "petos-auth-code-verifier"]) await Preferences.remove({ key: k }).catch(() => {});
    } catch { /* ignore */ }
    try { localStorage.removeItem("petos-auth"); } catch { /* ignore */ }
    try { window.__petosAccessToken = null; } catch { /* ignore */ }
    return;
  }
  // Two-tier signOut. Default scope hits POST /logout with the current
  // access token; when the token is already expired/invalid the server
  // returns 401 and supabase-js throws — leaving the local session in
  // storage and the UI stuck because doSignOut never reaches its state
  // clear. Fall back to scope:"local" which only wipes the persisted
  // session, so logout always succeeds even from a zombie state.
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return;
  } catch (e) {
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch { /* last resort — handled by caller */ }
    // Best-effort: clear the persisted token so a refresh can't restore it.
    try {
      if (typeof localStorage !== "undefined") localStorage.removeItem("petos-auth");
    } catch { /* ignore */ }
    try { window.__petosAccessToken = null; } catch { /* ignore */ }
    // Don't rethrow — the local session is gone; the caller's UI cleanup
    // is what the user actually wants. Log so we can still spot it.
    // eslint-disable-next-line no-console
    console.warn("[PettoBook] signOut server call failed, local session cleared:", e?.message || e);
  }
}

export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data?.session || null;
}

export async function getCurrentUser() {
  const session = await getSession();
  if (!session?.user) return null;
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", session.user.id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export function onAuthChange(cb) {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => cb(session));
  return () => data.subscription.unsubscribe();
}

// Send a password-reset email. Supabase emails a magic link that lands the
// user back on the app with a PASSWORD_RECOVERY event; App.jsx uses that to
// render the reset-password screen so the user can set a new password.
export async function sendPasswordReset(email) {
  const redirectTo = (typeof window !== "undefined" ? window.location.origin : "") + "/#/reset";
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
  if (error) throw error;
}

// Used by the reset-password screen after the recovery link drops the user in.
export async function updatePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

// Google OAuth — redirects to Google, then back to the app. Supabase handles
// the callback in the URL hash on return.
//
// We stash a localStorage "OAuth in flight" marker BEFORE handing control to
// Google. Supabase's `detectSessionInUrl: true` strips the access_token hash
// synchronously during client creation (module-load time), which fires before
// React's init can read it. Without the marker we'd have no way to know "we
// just came back from OAuth" if the callback failed (PKCE state lost, third-
// party cookies blocked, nonce already consumed). Init clears the marker once
// the session is established; the OAuth watchdog clears it on timeout.
export async function signInWithGoogle({ redirectTo } = {}) {
  const isNative = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
  // Pending-OAuth flag only matters for the web redirect flow (where the
  // WebView navigates away and we'd otherwise lose track of "OAuth is in
  // flight"). Native uses ASWebAuthSession which never leaves the app,
  // so the flag is pure noise — a stale one strands the watchdog on
  // the next cold start.
  if (!isNative) {
    try { localStorage.setItem("petos:oauth-pending", String(Date.now())); } catch { /* ignore */ }
  } else {
    try { localStorage.removeItem("petos:oauth-pending"); } catch { /* ignore */ }
  }
  if (isNative) {
    // Native OAuth. iOS uses ASWebAuthenticationSession (WebAuthPlugin —
    // auto-dismiss on callback scheme). Android uses Chrome Custom Tabs
    // via @capacitor/browser + the AndroidManifest intent-filter on
    // `com.pettobook.app` scheme, listening for appUrlOpen from
    // @capacitor/app to capture the callback URL.
    const isAndroid = window.Capacitor?.getPlatform?.() === "android";
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: "com.pettobook.app://login-callback",
          skipBrowserRedirect: true,
          queryParams: { access_type: "offline", prompt: "consent" },
        },
      });
      if (error || !data?.url) {
        try { localStorage.removeItem("petos:oauth-pending"); } catch { /* ignore */ }
        throw error || new Error("Google OAuth URL missing");
      }
      let callbackUrl = "";
      if (isAndroid) {
        const { Browser } = await import("@capacitor/browser");
        const { App } = await import("@capacitor/app");
        callbackUrl = await new Promise((resolve, reject) => {
          let listener;
          const timeoutId = setTimeout(() => {
            try { listener?.remove?.(); } catch { /* ignore */ }
            try { Browser.close(); } catch { /* ignore */ }
            reject(new Error("OAuth timed out"));
          }, 5 * 60 * 1000);
          App.addListener("appUrlOpen", (event) => {
            const url = event?.url || "";
            if (url.startsWith("com.pettobook.app://")) {
              clearTimeout(timeoutId);
              try { listener?.remove?.(); } catch { /* ignore */ }
              try { Browser.close(); } catch { /* ignore */ }
              resolve(url);
            }
          }).then((l) => { listener = l; });
          Browser.open({ url: data.url, presentationStyle: "popover" }).catch((e) => {
            clearTimeout(timeoutId);
            try { listener?.remove?.(); } catch { /* ignore */ }
            reject(e);
          });
        });
      } else {
        const { registerPlugin } = await import("@capacitor/core");
        const WebAuth = registerPlugin("WebAuth");
        const result = await WebAuth.openAuth({
          url: data.url,
          callbackScheme: "com.pettobook.app",
        });
        callbackUrl = result?.url || "";
      }
      const after = callbackUrl.split("//").slice(1).join("//");
      const tail = after.includes("#") ? after.split("#").slice(1).join("#")
                 : after.includes("?") ? after.split("?").slice(1).join("?")
                 : "";
      const params = new URLSearchParams(tail);
      const oauthError = params.get("error_description") || params.get("error");
      if (oauthError) throw new Error("OAuth 失敗: " + oauthError);
      const code = params.get("code");
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");
      if (accessToken && refreshToken) {
        try { window.__petosAccessToken = accessToken; } catch { /* ignore */ }
        const { error: setErr } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
        if (setErr) throw setErr;
      } else if (code) {
        const { data: sessData, error: exErr } = await supabase.auth.exchangeCodeForSession(code);
        if (exErr) throw new Error("exchangeCodeForSession 失敗: " + (exErr.message || exErr));
        if (!sessData?.session) throw new Error("無 session, callbackUrl=" + callbackUrl.slice(0, 160));
      } else {
        throw new Error("callback 無 code/token, url=" + callbackUrl.slice(0, 200));
      }
      try { localStorage.removeItem("petos:oauth-pending"); } catch { /* ignore */ }
      // After ASWebAuth dismissal the Capacitor WebView's HTTP/2 connection
      // pool to Supabase is dead — every subsequent fetch hangs past
      // PROFILE_TIMEOUT_MS even though Supabase is fine. Hard-reload the
      // WebView: session is persisted in Capacitor Preferences, so on the
      // fresh page load supabase-js sees an existing session and boots
      // straight into the dashboard.
      window.location.replace(window.location.origin + "/");
      return;
    } catch (e) {
      try { localStorage.removeItem("petos:oauth-pending"); } catch { /* ignore */ }
      throw e;
    }
  }
  // Web: same-tab redirect back to the current page.
  const target = redirectTo || window.location.href;
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: target, queryParams: { access_type: "offline", prompt: "consent" } },
  });
  if (error) {
    try { localStorage.removeItem("petos:oauth-pending"); } catch { /* ignore */ }
    throw error;
  }
}

// Apple Sign In — Face ID / Touch ID on iPad, no keyboard required. Same
// OAuth round-trip pattern as Google: native uses the in-app browser + custom
// scheme, web uses the standard redirect. Supabase Apple provider must be
// enabled in the dashboard (Auth → Providers → Apple) with the Services ID
// and key configured per Apple Developer.
export async function signInWithApple({ redirectTo } = {}) {
  const isNative = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
  if (!isNative) {
    try { localStorage.setItem("petos:oauth-pending", String(Date.now())); } catch { /* ignore */ }
  }
  if (isNative) {
    // Required by App Store Review Guideline 4.8: any app that offers
    // third-party social login (Google, Facebook, etc.) MUST also offer
    // Apple Sign In on iOS. Same ASWebAuth / Chrome Custom Tabs path as
    // Google — see signInWithGoogle for the architectural notes.
    const isAndroid = window.Capacitor?.getPlatform?.() === "android";
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "apple",
        options: {
          redirectTo: "com.pettobook.app://login-callback",
          skipBrowserRedirect: true,
        },
      });
      if (error || !data?.url) throw error || new Error("Apple OAuth URL missing");
      let callbackUrl = "";
      if (isAndroid) {
        const { Browser } = await import("@capacitor/browser");
        const { App } = await import("@capacitor/app");
        callbackUrl = await new Promise((resolve, reject) => {
          let listener;
          const timeoutId = setTimeout(() => {
            try { listener?.remove?.(); } catch { /* ignore */ }
            try { Browser.close(); } catch { /* ignore */ }
            reject(new Error("OAuth timed out"));
          }, 5 * 60 * 1000);
          App.addListener("appUrlOpen", (event) => {
            const url = event?.url || "";
            if (url.startsWith("com.pettobook.app://")) {
              clearTimeout(timeoutId);
              try { listener?.remove?.(); } catch { /* ignore */ }
              try { Browser.close(); } catch { /* ignore */ }
              resolve(url);
            }
          }).then((l) => { listener = l; });
          Browser.open({ url: data.url, presentationStyle: "popover" }).catch((e) => {
            clearTimeout(timeoutId);
            try { listener?.remove?.(); } catch { /* ignore */ }
            reject(e);
          });
        });
      } else {
        const { registerPlugin } = await import("@capacitor/core");
        const WebAuth = registerPlugin("WebAuth");
        const result = await WebAuth.openAuth({
          url: data.url,
          callbackScheme: "com.pettobook.app",
        });
        callbackUrl = result?.url || "";
      }
      const after = callbackUrl.split("//").slice(1).join("//");
      const tail = after.includes("#") ? after.split("#").slice(1).join("#")
                 : after.includes("?") ? after.split("?").slice(1).join("?")
                 : "";
      const params = new URLSearchParams(tail);
      const oauthError = params.get("error_description") || params.get("error");
      if (oauthError) throw new Error("OAuth 失敗: " + oauthError);
      const code = params.get("code");
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");
      if (accessToken && refreshToken) {
        try { window.__petosAccessToken = accessToken; } catch { /* ignore */ }
        const { error: setErr } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
        if (setErr) throw setErr;
      } else if (code) {
        const { data: sessData, error: exErr } = await supabase.auth.exchangeCodeForSession(code);
        if (exErr) throw new Error("exchangeCodeForSession 失敗: " + (exErr.message || exErr));
        if (!sessData?.session) throw new Error("無 session, callbackUrl=" + callbackUrl.slice(0, 160));
      } else {
        throw new Error("callback 無 code/token, url=" + callbackUrl.slice(0, 200));
      }
      window.location.replace(window.location.origin + "/");
      return;
    } catch (e) {
      throw e;
    }
  }
  const target = redirectTo || window.location.href;
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "apple",
    options: { redirectTo: target },
  });
  if (error) {
    try { localStorage.removeItem("petos:oauth-pending"); } catch { /* ignore */ }
    throw error;
  }
}
